/**
 * Digit helpers. The UI keeps ASCII digits in data and in the DOM; IRANYekanX `ss02`
 * draws them as Persian digits. Use `toPersianDigits` only where no font features apply
 * (document.title, native dialogs, generated files).
 *
 * Normalization comes from `@mfa-aff/shared` (the same code the API and the WHMCS bridge use);
 * the grouping and percent helpers below are display-only.
 */
import { toAsciiDigits, toPersianDigits } from "@mfa-aff/shared";

export { toPersianDigits, toAsciiDigits };

/** Persian/Arabic-Indic digits to ASCII; everything else untouched (alias of shared `toAsciiDigits`). */
export const normalizeDigits = (input: string): string => toAsciiDigits(input);

/** Groups an integer string with the Arabic thousands separator (U+066C), e.g. 3٬400٬000. */
export function groupThousands(digits: string): string {
    const negative = digits.startsWith("-");
    const s = negative ? digits.slice(1) : digits;
    const groups: string[] = [];
    for (let i = s.length; i > 0; i -= 3) {
        groups.unshift(s.slice(Math.max(0, i - 3), i));
    }
    return (negative ? "-" : "") + groups.join("٬");
}

/** Formats a plain integer (number or bigint) with Persian grouping. */
export function formatNumber(value: number | bigint): string {
    const n = typeof value === "number" ? Math.trunc(value) : value;
    return groupThousands(n.toString());
}

/**
 * Formats a rate in basis points (1% = 100) as a Persian percentage: 2200 → "22٪", 2250 → "22٫5٪".
 */
export function formatPercentBp(bp: number): string {
    const whole = Math.trunc(bp / 100);
    const frac = Math.abs(bp % 100);
    if (frac === 0) return `${whole}٪`;
    const fracStr = String(frac).padStart(2, "0").replace(/0+$/, "");
    return `${whole}٫${fracStr}٪`;
}

/** Formats a ratio 0..1 as a Persian percentage with one decimal when needed. */
export function formatRatio(ratio: number, fractionDigits = 1): string {
    const v = Math.round(ratio * 100 * 10 ** fractionDigits) / 10 ** fractionDigits;
    const [i, f] = String(v).split(".");
    return f ? `${i}٫${f}٪` : `${i}٪`;
}
